/**
 * Excel Snapshot Module
 *
 * Captures, stores, and restores Excel workbook state for versioning.
 * Snapshots are saved to IndexedDB with FIFO eviction.
 */

import {
  columnLetterToNumber,
  columnNumberToLetter,
  parseCellAddress,
} from '../agents/tool-utils'
import { getExcelRuntime } from '../excel-utils'
import type { CellSnapshot, SheetSnapshot, Snapshot } from './snapshot-db'

// Re-export types and storage functions from IndexedDB module
export type {
  CellFormat,
  CellSnapshot,
  SheetSnapshot,
  Snapshot,
} from './snapshot-db'

export {
  associateTurnWithAfterSnapshot,
  associateTurnWithSnapshot,
  clearAllSnapshots,
  deleteSnapshot,
  getAfterSnapshotIdForTurn,
  getAllSnapshotIds,
  getSnapshot,
  getSnapshotForTurn,
  getSnapshotIdForTurn,
  getStorageInfo,
  saveSnapshot,
} from './snapshot-db'

// ============================================================================
// Constants
// ============================================================================

const MAX_CELLS_PER_SHEET = 50000

// ============================================================================
// Capture Snapshot
// ============================================================================

/**
 * Capture the current state of the workbook
 */
export async function captureSnapshot(): Promise<Snapshot> {
  const snapshotId = Date.now().toString()
  const sheets: SheetSnapshot[] = []

  const Excel = getExcelRuntime()

  await Excel.run(async (context: Excel.RequestContext) => {
    const worksheets = context.workbook.worksheets
    worksheets.load('items')
    await context.sync()

    // Load properties for all sheets first
    for (const sheet of worksheets.items) {
      sheet.load(['id', 'name'])
    }
    await context.sync()

    // Batch: load all usedRanges in a single sync
    const usedRanges: Excel.Range[] = []
    for (const sheet of worksheets.items) {
      const usedRange = sheet.getUsedRangeOrNullObject()
      usedRange.load([
        'address',
        'values',
        'formulas',
        'numberFormat',
        'rowCount',
        'columnCount',
      ])
      usedRanges.push(usedRange)
    }
    await context.sync()

    // Load cell properties for format data (fill, font, alignment)
    type CellProps = Array<Array<{ format?: Record<string, unknown> }>>
    const cellPropsValues: Array<CellProps | null> = usedRanges.map(() => null)
    try {
      const cellPropsLoads: Array<OfficeExtension.ClientResult<
        Excel.CellProperties[][]
      > | null> = []
      let hasLoads = false
      for (const usedRange of usedRanges) {
        if (!usedRange.address) {
          cellPropsLoads.push(null)
          continue
        }
        cellPropsLoads.push(
          usedRange.getCellProperties({
            format: {
              fill: { color: true },
              font: { bold: true, italic: true, color: true, size: true },
              horizontalAlignment: true,
              verticalAlignment: true,
              wrapText: true,
            },
          })
        )
        hasLoads = true
      }
      if (hasLoads) {
        await context.sync()
        for (let idx = 0; idx < cellPropsLoads.length; idx++) {
          const load = cellPropsLoads[idx]
          if (load) {
            cellPropsValues[idx] = load.value as unknown as CellProps
          }
        }
      }
    } catch (e) {
      console.warn(
        '[Snapshot] getCellProperties failed, format data unavailable',
        e
      )
    }

    // Process all loaded ranges
    for (let i = 0; i < worksheets.items.length; i++) {
      const sheet = worksheets.items[i]
      const usedRange = usedRanges[i]

      // Skip sheets with no data
      if (!usedRange.address) {
        sheets.push({
          id: sheet.id,
          name: sheet.name,
          cells: {},
          usedRangeAddress: '',
        })
        continue
      }

      // Check cell count limit
      const totalCells = usedRange.rowCount * usedRange.columnCount
      if (totalCells > MAX_CELLS_PER_SHEET) {
        console.warn(
          `Sheet "${sheet.name}" has ${totalCells} cells, limiting to ${MAX_CELLS_PER_SHEET}`
        )
      }

      const cells: Record<string, CellSnapshot> = {}
      const values = usedRange.values as unknown[][]
      const formulas = usedRange.formulas as string[][]
      const numberFormats = usedRange.numberFormat as string[][]
      const cellProps = cellPropsValues[i]

      // Parse start cell from address (e.g., "Sheet1!A1:C10" → "A1")
      const addressParts = usedRange.address.split('!')
      const rangeAddress = addressParts[1] || addressParts[0]
      const startAddress = rangeAddress.split(':')[0]
      const { col: startCol, row: startRow } = parseCellAddress(startAddress)
      const startColNum = columnLetterToNumber(startCol)

      let cellCount = 0
      for (
        let r = 0;
        r < values.length && cellCount < MAX_CELLS_PER_SHEET;
        r++
      ) {
        for (
          let c = 0;
          c < values[r].length && cellCount < MAX_CELLS_PER_SHEET;
          c++
        ) {
          const value = values[r][c]
          const formulaRaw = formulas[r][c]
          // Formula can be non-string (number, boolean) if cell has no formula
          const formula =
            typeof formulaRaw === 'string' && formulaRaw.startsWith('=')
              ? formulaRaw
              : null

          // Only store non-empty cells
          if (value === '' && !formula) {
            continue
          }

          const cellAddress = `${columnNumberToLetter(startColNum + c)}${startRow + r}`

          const props = cellProps?.[r]?.[c]?.format
          const fill = props?.fill as { color?: string } | undefined
          const font = props?.font as
            | {
                bold?: boolean
                italic?: boolean
                color?: string
                size?: number
              }
            | undefined
          const nf = numberFormats?.[r]?.[c]

          cells[cellAddress] = {
            value,
            formula,
            format: {
              backgroundColor: fill?.color || null,
              fontColor: font?.color || null,
              fontBold: font?.bold ?? false,
              fontItalic: font?.italic ?? false,
              fontSize: font?.size ?? null,
              numberFormat: nf && nf !== 'General' ? nf : null,
              horizontalAlignment:
                (props?.horizontalAlignment as string) ?? null,
              verticalAlignment: (props?.verticalAlignment as string) ?? null,
              wrapText: (props?.wrapText as boolean) ?? false,
            },
          }
          cellCount++
        }
      }

      sheets.push({
        id: sheet.id,
        name: sheet.name,
        cells,
        usedRangeAddress: usedRange.address,
      })
    }
  })

  return {
    id: snapshotId,
    turnId: null,
    timestamp: Date.now(),
    sheets,
  }
}

// ============================================================================
// Restore Snapshot
// ============================================================================

/**
 * Calculate bounding box from cell addresses
 * Returns min/max row and column numbers
 */
function calculateBoundingBox(
  cells: Record<string, CellSnapshot>
): { minRow: number; maxRow: number; minCol: number; maxCol: number } | null {
  const addresses = Object.keys(cells)
  if (addresses.length === 0) return null

  let minRow = Number.MAX_SAFE_INTEGER
  let maxRow = 0
  let minCol = Number.MAX_SAFE_INTEGER
  let maxCol = 0

  for (const address of addresses) {
    const { col, row } = parseCellAddress(address)
    const colNum = columnLetterToNumber(col)
    minRow = Math.min(minRow, row)
    maxRow = Math.max(maxRow, row)
    minCol = Math.min(minCol, colNum)
    maxCol = Math.max(maxCol, colNum)
  }

  return { minRow, maxRow, minCol, maxCol }
}

/**
 * Build 2D arrays for batch restore
 * Returns separate values and formulas arrays to handle Excel's API correctly:
 * - values: Contains all cell values (numbers, strings, booleans stay typed correctly)
 * - formulas: Contains formulas only (null for non-formula cells, to be skipped)
 * - formulaAddresses: List of addresses that have formulas (for batch setting)
 */
function buildRestoreData(
  cells: Record<string, CellSnapshot>,
  boundingBox: {
    minRow: number
    maxRow: number
    minCol: number
    maxCol: number
  }
): {
  values: unknown[][]
  formulas: (string | null)[][]
  hasFormulas: boolean
  numberFormats: string[][]
  hasNumberFormats: boolean
} {
  const { minRow, maxRow, minCol, maxCol } = boundingBox
  const rowCount = maxRow - minRow + 1
  const colCount = maxCol - minCol + 1

  // Initialize with empty strings (Excel treats '' as empty cell)
  const values: unknown[][] = Array.from({ length: rowCount }, () =>
    Array.from({ length: colCount }, () => '')
  )

  // Build a parallel 2D formulas array — null means "keep the value"
  const formulas: (string | null)[][] = Array.from({ length: rowCount }, () =>
    Array.from({ length: colCount }, () => null)
  )
  let hasFormulas = false

  // Number formats (default "General")
  const numberFormats: string[][] = Array.from({ length: rowCount }, () =>
    Array.from({ length: colCount }, () => 'General')
  )
  let hasNumberFormats = false

  for (const [address, cellData] of Object.entries(cells)) {
    const { col, row } = parseCellAddress(address)
    const colNum = columnLetterToNumber(col)
    const rowIndex = row - minRow
    const colIndex = colNum - minCol

    // Always set the value (for formula cells, this is the calculated value)
    values[rowIndex][colIndex] = cellData.value

    // Track formula cells in the 2D array for batch setting
    if (cellData.formula) {
      formulas[rowIndex][colIndex] = cellData.formula
      hasFormulas = true
    }

    // Number format
    if (cellData.format.numberFormat) {
      numberFormats[rowIndex][colIndex] = cellData.format.numberFormat
      hasNumberFormats = true
    }
  }

  return {
    values,
    formulas,
    hasFormulas,
    numberFormats,
    hasNumberFormats,
  }
}

/**
 * Restore the workbook to a previous snapshot state
 * Uses batch operations for performance (sets entire ranges at once)
 */
export async function restoreWorkbookSnapshot(
  snapshot: Snapshot
): Promise<void> {
  const Excel = getExcelRuntime()

  console.log(`[RESTORE] Starting restore for snapshot ${snapshot.id}`)
  console.log(`[RESTORE] Snapshot has ${snapshot.sheets.length} sheets`)

  await Excel.run(async (context: Excel.RequestContext) => {
    const worksheets = context.workbook.worksheets
    worksheets.load('items')
    await context.sync()

    console.log(
      `[RESTORE] Found ${worksheets.items.length} worksheets in workbook`
    )

    // Load properties for all sheets
    for (const sheet of worksheets.items) {
      sheet.load(['id', 'name'])
    }
    await context.sync()

    // Step 1: Delete sheets that were added since snapshot
    const snapshotSheetIds = new Set(snapshot.sheets.map((s) => s.id))
    const sheetsToDelete: Excel.Worksheet[] = []
    for (const currentSheet of worksheets.items) {
      if (!snapshotSheetIds.has(currentSheet.id)) {
        console.log(
          `[RESTORE] Sheet "${currentSheet.name}" was added since snapshot, will be deleted`
        )
        sheetsToDelete.push(currentSheet)
      }
    }

    // Delete added sheets (must be done before creating new ones to avoid conflicts)
    for (const sheetToDelete of sheetsToDelete) {
      try {
        sheetToDelete.delete()
        console.log(`[RESTORE] Deleted added sheet: "${sheetToDelete.name}"`)
      } catch (error) {
        console.error(
          `[RESTORE] Failed to delete sheet "${sheetToDelete.name}"`,
          error
        )
      }
    }
    await context.sync()

    // Reload worksheets after deletions
    worksheets.load('items')
    await context.sync()
    for (const sheet of worksheets.items) {
      sheet.load(['id', 'name'])
    }
    await context.sync()

    // Step 2: Restore existing sheets and create missing ones
    for (const sheetSnapshot of snapshot.sheets) {
      console.log(
        `[RESTORE] Processing sheet: "${sheetSnapshot.name}" (${sheetSnapshot.id})`
      )

      // Find sheet by ID first, then by name if ID doesn't match (handles renamed sheets)
      let sheet = worksheets.items.find((s) => s.id === sheetSnapshot.id)

      // If not found by ID, try to find by name (sheet might have been recreated)
      if (!sheet) {
        sheet = worksheets.items.find((s) => s.name === sheetSnapshot.name)
      }

      // If still not found, create the sheet
      if (!sheet) {
        console.log(
          `[RESTORE] Sheet "${sheetSnapshot.name}" not found, creating it`
        )
        try {
          sheet = worksheets.add(sheetSnapshot.name)
          await context.sync()
          console.log(`[RESTORE] Created sheet: "${sheetSnapshot.name}"`)
        } catch (error) {
          console.error(
            `[RESTORE] Failed to create sheet "${sheetSnapshot.name}"`,
            error
          )
          continue
        }
      }

      // Restore sheet name if it was renamed (found by ID but name differs)
      if (sheet.name !== sheetSnapshot.name) {
        console.log(
          `[RESTORE] Sheet was renamed from "${sheetSnapshot.name}" to "${sheet.name}", restoring original name`
        )
        sheet.name = sheetSnapshot.name
        await context.sync()
      }

      const cellCount = Object.keys(sheetSnapshot.cells).length
      console.log(
        `[RESTORE] Restoring ${cellCount} cells in sheet "${sheetSnapshot.name}"`
      )

      // Get CURRENT used range (includes any newly added cells since snapshot)
      const currentUsedRange = sheet.getUsedRangeOrNullObject()
      currentUsedRange.load('address')
      await context.sync()

      // Clear the entire current used range to remove newly added cells
      if (currentUsedRange.address) {
        const addressParts = currentUsedRange.address.split('!')
        const currentRangeAddress = addressParts[1] || addressParts[0]
        console.log(
          `[RESTORE] Current used range: ${currentRangeAddress} (snapshot had: ${sheetSnapshot.usedRangeAddress || 'none'})`
        )
        console.log(
          `[RESTORE] Clearing entire current used range to remove newly added cells...`
        )
        const clearRange = sheet.getRange(currentRangeAddress)
        clearRange.clear('Contents')
        await context.sync()
        console.log(
          `[RESTORE] Cleared current used range ${currentRangeAddress}`
        )
      } else {
        console.log(
          `[RESTORE] No current used range found (sheet is empty), skipping clear`
        )
      }

      // Skip if no cells to restore
      if (cellCount === 0) {
        console.log(
          `[RESTORE] No cells to restore in sheet "${sheetSnapshot.name}"`
        )
        continue
      }

      // Calculate bounding box and build 2D arrays for batch restore
      const boundingBox = calculateBoundingBox(sheetSnapshot.cells)
      if (!boundingBox) {
        console.log(
          `[RESTORE] No bounding box for sheet "${sheetSnapshot.name}"`
        )
        continue
      }

      const { minRow, maxRow, minCol, maxCol } = boundingBox
      const startAddress = `${columnNumberToLetter(minCol)}${minRow}`
      const endAddress = `${columnNumberToLetter(maxCol)}${maxRow}`
      const rangeAddress =
        startAddress === endAddress
          ? startAddress
          : `${startAddress}:${endAddress}`

      console.log(
        `[RESTORE] Batch restoring range ${rangeAddress} (${cellCount} cells)`
      )

      // Build data arrays
      const { values, formulas, hasFormulas, numberFormats, hasNumberFormats } =
        buildRestoreData(sheetSnapshot.cells, boundingBox)

      // Step 1: Set all values at once (preserves number/boolean types)
      const range = sheet.getRange(rangeAddress)
      range.values = values

      // Step 2: Set formulas in batch (overwrites values for formula cells)
      if (hasFormulas) {
        const formulaCount = formulas.flat().filter(Boolean).length
        console.log(
          `[RESTORE] Setting ${formulaCount} formulas in sheet "${sheetSnapshot.name}"`
        )
        const formulasArray: string[][] = formulas.map((row, r) =>
          row.map((f, c) => f ?? String(values[r][c] ?? ''))
        )
        range.formulas = formulasArray
      }

      // Step 3: Restore number formats (skip if all "General")
      if (hasNumberFormats) {
        range.numberFormat = numberFormats
      }

      // Note: Formatting (fill, font, alignment) is preserved by clear('Contents')
      // which only clears values/formulas without touching cell formatting.
      await context.sync()

      console.log(
        `[RESTORE] Restored ${cellCount} cells in sheet "${sheetSnapshot.name}"`
      )
    }
  })

  console.log(
    `[RESTORE] Successfully restored workbook to snapshot ${snapshot.id}`
  )
}
